import { useNavigate, useParams } from "react-router-dom"
import { useGetModelFamiliesModelFamilyGet, useGetModelFamilyModelsMetadataModelFamilyModelsModelFamilyIdGet } from "../../../api/generated/fastAPI";
import { useEffect, useState, MouseEvent, ReactNode, useMemo } from "react";
import { Model, ModelFamily } from "../../../api/models";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import Dropdown, { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import useMessage from "../../../hooks/useMessage";
import SearchBar from "../../../components/Filtering/SearchBar";
import SnackBar from "../../../components/Alerts/SnackBar";
import DataTable, { TableData, TableDataField, TableHeader } from "../../../components/Table/DataTable";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import ListGridToggle from "../../../components/Buttons/ListGridToggle";
import { convertNumberToDecimal, exportData } from "../../../utils/formattingFns";
import DataGrid from "../../../components/Grid/DataGrid";
import BatchPredictionModal from "../../Resultsets/components/BatchPredictionModal";
import DeleteModal from "../../../components/Popups/DeleteModal";
import SingleCompoundPredictionModal from "../../Resultsets/components/SingleCompoundPredictionModal";
import useDelete from "../../../hooks/endpoints/useDelete";
import EmptyRecords from "../../../components/Alerts/EmptyRecords";
import { useAccess } from "../../../access/useAccess";
import { demo_access } from "../../../access/predefinedConditions";

import { ReactComponent as RecordsIcon } from '../../../assets/Icons/adornments/Records.svg';
import { ReactComponent as FingerprintIcon } from '../../../assets/Icons/adornments/Fingerprint.svg';
import { formatDisplayedModelFamilyMetadata, formatModelData, getModelTaskType, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import useDownload from "../../../hooks/endpoints/useDownload";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";
import ModelComparisonV2 from "../components/ModelComparisonV2";
import { ReactComponent as LowIcon } from '../../../assets/Icons/arrows/arrow-down.svg';
import { ReactComponent as HighIcon } from '../../../assets/Icons/arrows/arrow-up.svg';

export const classificationHeaders: TableHeader[] = [
    {
        label: 'Method',
        value: 'method',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'AUC',
        value: 'auc',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'F1 Score',
        value: 'f1score',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Precision',
        value: 'precision',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Recall',
        value: 'recall',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Accuracy',
        value: 'acc',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Specificity',
        value: 'specificity',
        isShown: true,
        alignment: 'center',
    },
    {
        label: "Cohen's Kappa",
        value: 'cohens_kappa',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'MCC',
        value: 'mcc',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'True Positives',
        value: 'tp',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'True Negatives',
        value: 'tn',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'False Positives',
        value: 'fp',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'False Negatives',
        value: 'fn',
        isShown: true,
        alignment: 'center',
    },
]

export const regressionHeaders: TableHeader[] = [
    {
        label: 'Method',
        value: 'method',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'MGD',
        value: 'mgd',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'MPD',
        value: 'mpd',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'MAE',
        value: 'mae',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'R2',
        value: 'r2',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'RMSE',
        value: 'rmse',
        isShown: true,
        alignment: 'center',
    },
]

export default function IndividualModelFamily() {
    const { id } = useParams();
    const navigate = useNavigate();
    const getModelFamilyMutation = useGetModelFamiliesModelFamilyGet({ 
        model_family_id: id,
        include_airflow_metadata: false 
    });
    const getModelFamilyModelsMutation = useGetModelFamilyModelsMetadataModelFamilyModelsModelFamilyIdGet(id ? Number(id) : 0);
    const { canAccess } = useAccess();

    const [ modelFamily, setModelFamily ] = useState<ModelFamily | null>(null);
    const [ modelFamilyModels, setModelFamilyModels ] = useState<Model[]>([]);
    const [ modelData, setModelData ] = useState<TableData[]>([]);
    const [ modelFamilyMetadata, setModelFamilyMetadata ] = useState<MetadataMenuItemType[]>([]);

    const [ headers, setHeaders ] = useState<TableHeader[]>([]);
    const [ isGrid, setIsGrid ] = useState(false);
    const [ selectedItems, setSelectedItems ] = useState<(string | number)[]>([]);
    const [ actionDropdownOptions, setActionDropdownOptions ] = useState<DropdownOption[]>([]);
    const { downloadModelFamilyStats, downloadModelStats } = useDownload();

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();
    

    const { deleteModelFamilies } = useDelete({ setPopupMessage });
    
    useEffect(() => {
        if (id && getModelFamilyMutation.data) {
            const data = getModelFamilyMutation.data as unknown as ModelFamily;
            setModelFamily(data);
        }
    }, [id, getModelFamilyMutation.data]);

    useEffect(() => {
        if (getModelFamilyModelsMutation.data) {
            const data = getModelFamilyModelsMutation.data as unknown as Model[];
            setModelFamilyModels(data);

            let { modelData: allModelData, modelHeaders } = formatModelData(data);
            setHeaders(modelHeaders);

            allModelData = allModelData.map(mdlData => {
                const fields: TableDataField[] = mdlData.fields.map(field => {
                    if (field.associatedColumn === 'method') {
                        return {
                            ...field,
                            action: (e: any) => navigate(`/models/${id}/${mdlData.id}`),
                        }
                    } else {
                        return {...field}
                    }
                });

                const model = data.find(d => d.file_hash! === mdlData.id);
                const options: DropdownOption[] = model ? injectOptions(model, mdlData) : [];

                return {
                    ...mdlData,
                    fields,
                    options,
                };
            });

            setModelData(allModelData);
        };

        //eslint-disable-next-line
    }, [getModelFamilyModelsMutation.data, id, navigate]);


    useEffect(() => {
        const fetchModelFamilyMetadata = async (family: ModelFamily, mdls: Model[]) => {
            const metadata = await formatDisplayedModelFamilyMetadata(family, mdls);
            setModelFamilyMetadata(metadata);
        }

        if (modelFamily) {
            fetchModelFamilyMetadata(modelFamily, modelFamilyModels);
        }
    }, [modelFamily, modelFamilyModels]);


    function injectOptions(model: Model, modelData: TableData) {
        const id = model.file_hash!;
        const nameField = modelData.fields.find(field => field.associatedColumn === 'method');
        
        let name = model.name ?? '';
        if (nameField) {
            name = nameField.value as string;
        }

        let options: DropdownOption[] = [
            {
                label: 'Run Prediction',
                action: () => {},
            },
            {
                label: 'Export',
                action: async (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    let message = '';
                    let isSuccess = true;

                    const item = { name, hash: id };
                    try {
                        await exportData(
                            [item],
                            '.joblib',
                            'model',
                        );
                        message = `Model successfully downloaded`;
                        
                    } catch (err:any) {
                        message = 'There was an error downloading this model';
                        isSuccess = false;
                        console.log(err);
                    };

                    setPopupMessage(message, isSuccess);
                },
            },
            {
                label: 'Export Model Stats',
                action: async (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    let message = '';
                    let isSuccess = true;

                    try {
                        await downloadModelStats(
                            '.xlsx',
                            model,
                            modelData,
                        )
                        message = 'Model stats downloaded successfully';
                    } catch (err:any) {
                        console.log(err);
                        message = 'There was an error downloading this model';
                        isSuccess = false;
                    }

                    setPopupMessage(message, isSuccess);
                }
            }
        ]

        if (canAccess({or: demo_access})) options.push(
            {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={[{
                            type: 'model',
                            id,
                            name,
                        }]}
                    />
                )
            }
        )
        return options;
    }

    let dropdownOptions: DropdownOption[] = [
        {
            label: 'Run Batch Prediction',
            btnComp: (
                <BatchPredictionModal
                    selectedModelFamilyIds={[Number(id)]}
                    setPopupMessage={setPopupMessage}
                    isPrimaryMenu={false}
                />
            )
        },
        {
            label: 'Run Single Prediction',
            btnComp: (
                <SingleCompoundPredictionModal
                    selectedModelFamilyIds={[Number(id)]}
                    setPopupMessage={setPopupMessage}
                    location="dropdown"
                />
            )
        },
        {
            label: 'Export Model Family Stats',
            action: async (e:MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();

                if (modelFamily && modelFamilyModels.length > 0 && modelData.length > 0) {
                    let message = '';
                    let isSuccess = true;

                    try {
                        await downloadModelFamilyStats(
                            '.xlsx',
                            modelFamily,
                            modelFamilyModels,
                            modelData,
                        );
                        message = 'Model family stats successfully downloaded'
                    } catch (err:any) {
                        console.log(err);
                        message = 'There was an error downloading this model family';
                        isSuccess = false;
                    }

                    setPopupMessage(message, isSuccess);
                }
            },
        },

    ];
    if (canAccess({or: demo_access})) dropdownOptions.push(
        {
            label: 'Delete',
            btnComp: (
                <DeleteModal 
                    items={[{
                        type: 'model',
                        id: id ? Number(id) : 0,
                        name: modelFamily && modelFamily.name ? modelFamily.name : '',
                    }]}
                    deleteFn={deleteModelFamilies}
                />
            )
        },
    )

    useEffect(() => {
        setActionDropdownOptions([
            {
                label: 'Export',
                action: async (e:MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    let message = '';
                    let isSuccess = true;

                    const items = selectedItems.length > 0 ? selectedItems.map(item => {
                        let name = '';
                        const matchingModel = modelFamilyModels.length > 0 ? modelFamilyModels.find(m => m.file_hash! === item) : undefined;
                        if (matchingModel) {
                            name = matchingModel.name!;
                        };
                        return { name, hash: item.toString() };
                    }) : [];
                    try {
                        await exportData(
                            items,
                            '.joblib',
                            'model',
                        );
                        setSelectedItems([]);
                        message = 'Models downloaded successfully';
                    
                    } catch (err:any) {
                        message = 'There was an error downloading these models';
                        isSuccess = false;
                        console.log(err);
                    };

                    setPopupMessage(message, isSuccess);
                },
            },
            {
                label: 'Export Model Stats',
                action: async (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();

                    if (modelData.length > 0 && modelFamilyModels.length > 0) {
                        let message = '';
                        let isSuccess = true;

                        const modelsAndData: {
                            model: Model,
                            data: TableData,
                        }[] = [];

                        selectedItems.forEach(item => {
                            const matchingModel = modelFamilyModels.find(m => m.file_hash! === item);
                            const matchingData = modelData.find(d => d.id === item);

                            if (matchingModel && matchingData) {
                                modelsAndData.push({
                                    model: matchingModel,
                                    data: matchingData,
                                })
                            };
                        });

                        const promises = modelsAndData.map(async (modelAndData) => {
                            const { model, data } = modelAndData;
                            return downloadModelStats(
                                '.xlsx',
                                model,
                                data,
                            )
                        });

                        try {
                            await Promise.all(promises);
                            setSelectedItems([]);
                            message = 'Model stats successfully downloaded';
                        } catch (err:any) {
                            console.log(err);
                            message = 'There was an error downloading this models';
                            isSuccess = false;
                        }
                        setPopupMessage(message, isSuccess);
                    }
                }
            },
            {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={selectedItems.length > 0 ? selectedItems.map(item => ({
                            type: 'model',
                            id: item,
                            name: '',
                        })) : []}
                        cancelFn={() => setSelectedItems([])}
                    />
                ),
            },
        ])

        //eslint-disable-next-line
    }, [selectedItems, modelFamilyModels]);

    const itemMetadata = useMemo(() => {
        if (modelFamilyModels.length === 0 || !modelFamily) return [];

        const metadata: {
            label: string,
            value: string | string[],
            adornment?: ReactNode,
        }[] = [{
            label: 'Records',
            value: modelFamilyModels.length.toString(),
            adornment: <RecordsIcon />,
        }];

        let descriptors: string[] = [];

        modelFamilyModels.forEach(model => {
            const { model_metadata } = model;
            const fingerprint_metadata = model_metadata?.fingerprint_metadata;

            if (fingerprint_metadata && fingerprint_metadata.job_metadata) {
                const job_metadata = fingerprint_metadata.job_metadata;
                const { fingerprint_functions } = job_metadata;

                if (fingerprint_functions && fingerprint_functions.length > 0) {
                    fingerprint_functions.forEach(fingerprint => descriptors.push(fingerprint.name ?? ''))
                };
            }
        });

        descriptors = Array.from(new Set(descriptors)).filter(desc => desc);

        if (descriptors.length > 0) {
            metadata.push({
                label: 'Fingerprints',
                value: descriptors,
                adornment: <FingerprintIcon />,
            })
        };

        const dcmCount = modelFamilyModels.filter(model => getModelTaskType(model) === 'dcm').length;
        if (dcmCount > 0) {
            metadata.push({
                label: 'DCM Models',
                value: dcmCount.toString(),
            });
        }

        const dsStats = modelFamily.dataset_statistics;

        if (dsStats) {
            const { actives, inactives, high_value, low_value, record_number } = dsStats;

            if (actives !== undefined && actives !== null && inactives !== undefined && inactives !== null) {
                metadata.push({
                    label: 'Actives',
                    value: actives.toString(),
                    adornment: <div className="bg-[#C7DB78] w-[16px] rounded-full aspect-square" />,
                });

                metadata.push({
                    label: 'Inactives',
                    value: inactives.toString(),
                    adornment: <div className="bg-[#FF8484] w-[16px] rounded-full aspect-square" />
                });
            } else if (high_value !== undefined && high_value !== null && low_value !== undefined && low_value !== null) {
                metadata.push({
                    label: 'High Value',
                    value: convertNumberToDecimal(high_value),
                    adornment: <HighIcon />
                });

                metadata.push({
                    label: 'Low Value',
                    value: convertNumberToDecimal(low_value),
                    adornment: <LowIcon />
                });
            }

            metadata.push({
                label: 'Training Set Records',
                value: record_number ? record_number.toString() : '0',
                adornment: <RecordsIcon />
            });
        }

        return metadata;


    }, [modelFamilyModels, modelFamily]);


    return (
        <div className="p-10 h-full flex flex-col justify-start items-stretch gap-4">

                <SinglePageHeader 
                    id='single-file-header' 
                    label={modelFamily ? modelFamily.name! : ''} 
                    isLoading={getModelFamilyMutation.isLoading || getModelFamilyModelsMutation.isLoading} 
                    pages={[
                        {
                            label: 'Model Families',
                            action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/models')}
                        },
                        {
                            label: modelFamily ? modelFamily.name! : '',
                        }
                    ]}
                    itemMetadata={itemMetadata}
                >
                    <Dropdown
                        btn={{
                            label: 'Options',
                            type: 'primary',
                            styling: 'solid',
                            id: 'single-file-options'
                        }}
                        options={dropdownOptions}
                    />
                    {modelFamilyMetadata.length > 0 && (
                        <UpdatedMetadataMenu 
                            menuLabel="Model Family Metadata"
                            menuItems={modelFamilyMetadata}
                            location="page"
                        />
                    )}
                </SinglePageHeader>

                {modelData.length > 0 ||  getModelFamilyMutation.isLoading || getModelFamilyModelsMutation.isLoading ? (
                <>

                    <ModelComparisonV2 
                        data={modelData}
                        isLoading={getModelFamilyMutation.isLoading || getModelFamilyModelsMutation.isLoading}
                    />

                    <div style={{maxHeight: '100vh'}} className="h-full overflow-hidden flex flex-col justify-start items-stretch gap-4">


                    <div className="w-full flex justify-between items-center gap-4">
                        <div className="max-w-[600px] w-full">
                            <SearchBar/>
                        </div>
                        <div className="flex justify-end items-center gap-4">
                            <ColumnsButton 
                                headers={headers} 
                                setHeaders={setHeaders} 
                                isDisabled={false}
                            />
                            {selectedItems.length > 0 && (
                                <Dropdown 
                                    btn={{
                                        label: `Actions (${selectedItems.length} selected)`,
                                        type: 'secondary',
                                        styling: 'solid',
                                        id: 'model-multi-action-btn',
                                    }}
                                    options={actionDropdownOptions}
                                />
                            )}
                            <ListGridToggle setIsGrid={setIsGrid} isGrid={isGrid}/>
                        </div>
                    </div>

                    <>
                    {isGrid ? (
                        <>
                            <DataGrid 
                                isLoading={getModelFamilyMutation.isLoading || getModelFamilyModelsMutation.isLoading} 
                                gridData={modelData}
                                headers={headers}
                                statsType="model"
                                selection={{
                                    selectedItems,
                                    setSelectedItems,
                                }}
                                setPopupMessage={setPopupMessage}
                            />
                        </>
                    ) : (
                        <>
                            <DataTable 
                                isLoading={getModelFamilyMutation.isLoading || getModelFamilyModelsMutation.isLoading} 
                                headers={headers} 
                                tableData={modelData}
                                // openDetails={openModelDetails}
                                statsType="model"
                                selection={{
                                    selectedItems,
                                    setSelectedItems,
                                }}
                                setPopupMessage={setPopupMessage}
                            />                  
                        </>
                        )}

                    </>
                    </div>

                </>

                ) : (
                    <EmptyRecords label="Model Family"/>
                )}

                
                <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  

        </div>
    )
}